import { ElementRef, Injectable } from '@angular/core';

/**
 * Scrolls a given element to the top of whatever scrolls it.
 *
 * The app's scroll container is the `mat-sidenav-content` of the main layout, NOT the window, so
 * `window.scrollTo` has no effect here. `scrollIntoView` walks up to the real scrollable ancestor on
 * its own, which makes the same call correct in the page, inside a card, and inside a dialog.
 *
 * The sticky app header would otherwise cover the first rows: that offset is compensated
 * declaratively with `scroll-margin-top` on the anchor element, not computed here.
 */
@Injectable({ providedIn: 'root' })
export class ScrollToTopService {
  // The user's opt-out (display.listScroll = 'keep'). Held as a plain field and PUSHED in by
  // ListScrollService rather than injected: this service is consumed by TableComponent,
  // TableEditRowsComponent and every ingest list, and the preference store needs Store + NgZone +
  // UserSettingsService, which would drag NgRx into the TestBed of every one of those components.
  // Defaulting to enabled also means a context that never wires the preference (a spec, a screen
  // rendered before the per-identity load lands) behaves as documented.
  private enabled = true;

  /** Apply the user's preference. Called by ListScrollService on load and on a cross-tab change. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  toTop(target: ElementRef<HTMLElement> | HTMLElement | null | undefined): void {
    if (!this.enabled) {
      return;
    }
    const el = target instanceof ElementRef ? target.nativeElement : target;
    // A detached or stubbed element (Karma) has no layout API -- degrade instead of throwing inside
    // the paging/sorting interaction that called us.
    if (!el || typeof el.scrollIntoView !== 'function') {
      return;
    }
    el.scrollIntoView({ block: 'start', inline: 'nearest' });
  }
}
